// apps/web/src/pages/coletor/kanban-tablet-page.tsx — Fase 11, Plano 15 (KAN-06, KAN-07, KAN-08, D-21, D-36, D-50) e Fase 17.9 (D-03..D-08, D-14).
// Tablet do operador: a fila da(s) célula(s) vinculada(s) ao dispositivo.
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import type { KanbanCartaoTablet, KanbanMotivo, KanbanTabletQuadro, KanbanTabletResultado } from '@pieltec/contracts';
import { ColetorBarraIdentidade } from '../../components/coletor/coletor-identidade';
import { TabletOperadorModal } from '../../components/coletor/tablet-operador-modal';
import { FilaLocalBadge } from '../../components/coletor/fila-local';
import { SolicitarMaterialDialog } from '../../components/coletor/solicitar-material-dialog';
import { BotaoSolicitarMaterialCartao, BotaoSolicitarMaterialSetor, type PedidoMaterialTablet } from '../../components/coletor/solicitar-material-acao';
import {
  cabecalhosDispositivo,
  devePurgarCredencial,
  limparCredencial,
  obterCredencial,
  obterQuadroCache,
  salvarQuadroCache,
  type CredencialDispositivo,
} from '../../lib/kanban/dispositivo';
import {
  acoesDoCartao,
  liberarChavesFinalizadas,
  montarEventoOffline,
  obterChaveConclusao,
  respostaEncerraConclusao,
  rotuloAguardandoAlocacao,
  rotuloEtapasAnteriores,
  type ValoresConclusao,
} from '../../lib/kanban/tablet';
import { buscarUrlStreamTablet } from '../../lib/kanban/operador-tablet';
import { postarTablet, type RespostaTablet } from '../../lib/kanban/tablet-api';
import {
  enfileirarEvento,
  listarPendentesPorAgregado,
  obterEstatisticasOutbox,
  sincronizarOutbox,
  type ResultadoSincronizacao,
} from '../../lib/offline/outbox';
import { intervaloRetryMs } from '../../lib/offline/sync-regras';
import { podeSolicitarMaterial } from '../../lib/coletor/celulas-coletor';
import { obterSessaoAtual } from '../../lib/session';
import { EVENTO_SESSAO_EXPIRADA } from '../../lib/atividade-sessao';
import { useKanbanSse } from '../planejamento/kanban/use-kanban-sse';
import { TabletCartao } from './kanban-tablet/tablet-cartao';
import { TabletConcluirSheet } from './kanban-tablet/tablet-concluir-sheet';
import { TabletEtapasAnteriores } from './kanban-tablet/tablet-etapas-anteriores';
import { TabletPausarSheet } from './kanban-tablet/tablet-pausar-sheet';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

const AVISO_OFFLINE = 'Apontamento salvo no tablet — será enviado ao reconectar.';
const AVISO_CARTAO_FORA_DA_LISTA = 'Este cartão foi alocado a outra cabine ou saiu da lista.';

const CLASSE_BOTAO =
  'min-h-[64px] min-w-[140px] px-6 font-bold text-base rounded-xl touch-manipulation text-white disabled:opacity-50';

function semRede(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Conecta o SSE do dispositivo com ticket efêmero (D-12, D-13, debounce de 300ms); só montado com credencial. */
function TabletTempoReal({
  credencial,
  onAtualizar,
  onNaoAutorizado,
}: {
  credencial: CredencialDispositivo;
  onAtualizar: () => void;
  onNaoAutorizado: () => void;
}): null {
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
  const agendar = useCallback(() => {
    if (temporizador.current) clearTimeout(temporizador.current);
    temporizador.current = setTimeout(onAtualizar, 300);
  }, [onAtualizar]);

  useEffect(
    () => () => {
      if (temporizador.current) clearTimeout(temporizador.current);
    },
    [],
  );

  // Referência estável: reabrir o SSE a cada render perderia eventos e zeraria o backoff (CR-03).
  const resolverUrl = useCallback(() => buscarUrlStreamTablet(credencial, API_BASE_URL), [credencial]);

  useKanbanSse({
    resolverUrl,
    onEvento: agendar,
    onReconectar: onAtualizar,
    onNaoAutorizado,
  });
  return null;
}

export function KanbanTabletPage(): ReactElement {
  const [credencial, setCredencial] = useState<CredencialDispositivo | null>(null);
  const [inicializado, setInicializado] = useState<boolean>(false);
  const [quadro, setQuadro] = useState<KanbanTabletQuadro | null>(null);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string | null>(null);
  const [revogado, setRevogado] = useState<boolean>(false);
  const [abaAtiva, setAbaAtiva] = useState<string | null>(null);
  const [emAndamento, setEmAndamento] = useState<string | null>(null);
  const [motivos, setMotivos] = useState<KanbanMotivo[]>([]);
  // D-14: a fila normal é a visão padrão; "Etapas anteriores" é uma visão separada da célula ativa.
  const [visao, setVisao] = useState<'fila' | 'etapas-anteriores'>('fila');
  // D-16/D-17: célula da lista de onde veio o "Registrar e avançar" (null = Concluir normal da fila).
  const [registroCelula, setRegistroCelula] = useState<string | null>(null);
  const [pausando, setPausando] = useState<KanbanCartaoTablet | null>(null);
  const [erroPausa, setErroPausa] = useState<string | null>(null);
  const [concluindo, setConcluindo] = useState<KanbanCartaoTablet | null>(null);
  const [chaveConclusao, setChaveConclusao] = useState<string | null>(null);
  // osId -> idempotencyId dos apontamentos salvos no tablet e ainda não enviados (CR-WEB-02).
  const [pendenciasOffline, setPendenciasOffline] = useState<ReadonlyMap<string, string>>(new Map());
  const [erroConclusao, setErroConclusao] = useState<string | null>(null);
  const [avisoOffline, setAvisoOffline] = useState<string | null>(null);
  const [avisoSync, setAvisoSync] = useState<string | null>(null);
  // Pedido de material ao almoxarifado (Fase 16): só com `estoque:solicitar` na sessão.
  const [podeSolicitar, setPodeSolicitar] = useState<boolean>(false);
  const [pedidoMaterial, setPedidoMaterial] = useState<PedidoMaterialTablet | null>(null);

  // Operador atual e modal de PIN (Fase 17.9, D-03..D-08).
  const [modalOperadorAberto, setModalOperadorAberto] = useState<boolean>(false);
  const [operadorNome, setOperadorNome] = useState<string | null>(null);
  const [acaoPendente, setAcaoPendente] = useState<{
    tipo: 'iniciar' | 'pausar' | 'concluir';
    executar: () => Promise<void>;
  } | null>(null);
  const acaoPendenteRef = useRef<{
    tipo: 'iniciar' | 'pausar' | 'concluir';
    executar: () => Promise<void>;
  } | null>(null);

  const atorRef = useRef<string | null>(null);
  const credencialRef = useRef<CredencialDispositivo | null>(null);
  const falhasSyncRef = useRef<number>(0);
  // Chave de idempotência por cartão: estável enquanto a conclusão não for confirmada (CR-WEB-02).
  const chavesConclusaoRef = useRef<Map<string, string>>(new Map());

  const purgarSeRevogado = useCallback(async (status: number, reason: string | null): Promise<boolean> => {
    if (!devePurgarCredencial({ status, reason: reason ?? undefined })) return false;
    await limparCredencial();
    credencialRef.current = null;
    setCredencial(null);
    setRevogado(true);
    return true;
  }, []);

  const carregarQuadro = useCallback(
    async (cred: CredencialDispositivo) => {
      setErro(null);
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/kanban/tablet/quadro`, {
          credentials: 'include',
          headers: cabecalhosDispositivo(cred),
        });
        if (res.status === 401) {
          const corpo = (await res.json().catch(() => ({}))) as { reason?: string };
          if (await purgarSeRevogado(401, corpo.reason ?? null)) return;
        }
        if (!res.ok) throw new Error('quadro');
        const dados = (await res.json()) as KanbanTabletQuadro;
        setQuadro(dados);
        void salvarQuadroCache(dados).catch(() => undefined);
      } catch {
        // Offline: mantém o quadro já carregado (ou o cache local) em vez de skeleton.
        setErro(semRede() ? null : 'Não foi possível carregar a fila da célula.');
      } finally {
        setCarregando(false);
      }
    },
    [purgarSeRevogado],
  );

  const carregarMotivos = useCallback(async (cred: CredencialDispositivo) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/kanban/tablet/motivos`, {
        credentials: 'include',
        headers: cabecalhosDispositivo(cred),
      });
      if (res.ok) setMotivos((await res.json()) as KanbanMotivo[]);
    } catch {
      // Sem rede a pausa fica sem a lista; o operador ainda pode usar "Outro".
    }
  }, []);

  const atualizarPendencias = useCallback(async (): Promise<void> => {
    try {
      setPendenciasOffline(await listarPendentesPorAgregado('Apontamento'));
    } catch {
      // Armazenamento local indisponível: mantém o último estado conhecido.
    }
  }, []);

  /**
   * Uma rodada de envio do outbox com o tratamento do resultado: purga a credencial em 401 de
   * dispositivo, avisa sessão expirada, parada por autor diferente e eventos recusados (D-14).
   */
  const sincronizarAgora = useCallback(async (): Promise<ResultadoSincronizacao | null> => {
    const cred = credencialRef.current;
    if (!cred) return null;
    let r: ResultadoSincronizacao;
    try {
      r = await sincronizarOutbox(cred.deviceId, cred.credential, {
        atorAtualId: atorRef.current ?? undefined,
      });
    } catch {
      // Falha ao ler o armazenamento local: tenta de novo no próximo ciclo.
      falhasSyncRef.current += 1;
      return null;
    }
    falhasSyncRef.current = r.parada ? falhasSyncRef.current + 1 : 0;
    liberarChavesFinalizadas(chavesConclusaoRef.current, r.chavesFinalizadas);
    void atualizarPendencias();
    if (r.parada === 'credencial') {
      await purgarSeRevogado(401, 'revoked');
      return r;
    }
    if (r.rejeitados > 0 || r.conflitos > 0) {
      setAvisoSync(
        'Há apontamentos salvos no tablet que o servidor recusou. Abra a Fila e chame um supervisor para revisá-los.',
      );
    } else if (r.parada === 'autor' && !atorRef.current) {
      setAvisoSync('Identifique-se com seu PIN para enviar os apontamentos salvos no tablet.');
    } else if (r.parada === 'autor') {
      setAvisoSync(
        'Há apontamentos salvos por outro operador no tablet. Conecte-se com a conta dele para enviar a fila pendente.',
      );
    } else if (r.parada === 'autenticacao') {
      setAvisoSync('Sessão expirada: entre com seu PIN para enviar os apontamentos salvos no tablet.');
    } else {
      setAvisoSync(null);
    }
    if (r.restantes === 0) setAvisoOffline(null);
    if (r.processados > 0 || r.rejeitados > 0 || r.conflitos > 0) void carregarQuadro(cred);
    return r;
  }, [atualizarPendencias, carregarQuadro, purgarSeRevogado]);

  useEffect(() => {
    let ativo = true;
    void obterCredencial()
      .then((cred) => {
        if (!ativo) return;
        setCredencial(cred);
        credencialRef.current = cred;
        setInicializado(true);
        if (!cred) {
          setCarregando(false);
          return;
        }
        void obterQuadroCache()
          .then((cache) => {
            if (!ativo || !cache) return;
            setQuadro((atual) => atual ?? cache);
            if (semRede()) setCarregando(false);
          })
          .catch(() => undefined);
        void carregarQuadro(cred);
        void carregarMotivos(cred);
        void atualizarPendencias();
        // O ator atual precisa estar resolvido ANTES do primeiro envio do outbox: ator desconhecido
        // com eventos de outro autor pararia a rodada (D-14, CR-02). `obterSessaoAtual` não lança.
        void obterSessaoAtual()
          .then((r) => {
            if (!ativo) return;
            if (r.autenticado) {
              atorRef.current = r.usuario.userId;
              setOperadorNome(r.usuario.displayName);
            } else {
              atorRef.current = null;
              setOperadorNome(null);
            }
            setPodeSolicitar(podeSolicitarMaterial(r));
          })
          .catch(() => undefined)
          .then(() => {
            if (ativo) void sincronizarAgora();
          });
      })
      .catch(() => {
        // IndexedDB indisponível (modo privado, cota, banco bloqueado por outra aba): sem isto o
        // tablet ficaria em skeleton para sempre.
        if (!ativo) return;
        setInicializado(true);
        setCarregando(false);
        setErro('Não foi possível acessar o armazenamento local do tablet.');
      });
    return () => {
      ativo = false;
    };
  }, [carregarQuadro, carregarMotivos, sincronizarAgora, atualizarPendencias]);

  // Limpa o operador em caso de sessão expirada pelo heartbeat (D-11).
  useEffect(() => {
    function aoSessaoExpirada(): void {
      atorRef.current = null;
      setOperadorNome(null);
      setPodeSolicitar(false);
    }
    window.addEventListener(EVENTO_SESSAO_EXPIRADA, aoSessaoExpirada);
    return () => {
      window.removeEventListener(EVENTO_SESSAO_EXPIRADA, aoSessaoExpirada);
    };
  }, []);

  // Gatilhos de reenvio: evento online, aba voltando a ficar visível e retry periódico com
  // backoff enquanto houver pendentes (navigator.onLine pode ser true com a API fora do ar).
  useEffect(() => {
    let ativo = true;
    let temporizador: ReturnType<typeof setTimeout> | null = null;

    function aoReconectar(): void {
      void sincronizarAgora().then((r) => {
        const cred = credencialRef.current;
        if (cred && r?.parada !== 'credencial') void carregarQuadro(cred);
      });
    }
    function aoMudarVisibilidade(): void {
      if (document.visibilityState === 'visible') void sincronizarAgora();
    }
    async function ciclo(): Promise<void> {
      if (!ativo) return;
      if (credencialRef.current) await sincronizarAgora();
      const estatisticas = await obterEstatisticasOutbox().catch(() => ({ pendentes: 1 }));
      if (!ativo) return;
      temporizador = setTimeout(() => void ciclo(), intervaloRetryMs(falhasSyncRef.current, estatisticas.pendentes > 0));
    }

    window.addEventListener('online', aoReconectar);
    document.addEventListener('visibilitychange', aoMudarVisibilidade);
    temporizador = setTimeout(() => void ciclo(), intervaloRetryMs(0, true));
    return () => {
      ativo = false;
      if (temporizador) clearTimeout(temporizador);
      window.removeEventListener('online', aoReconectar);
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
    };
  }, [carregarQuadro, sincronizarAgora]);

  const recarregar = useCallback(() => {
    const cred = credencialRef.current;
    if (cred) void carregarQuadro(cred);
  }, [carregarQuadro]);

  // Referência estável: o modal reinicia seu fluxo se o callback mudar a cada render (CR-01).
  const sincronizarParaModal = useCallback(async (): Promise<void> => {
    await sincronizarAgora();
  }, [sincronizarAgora]);

  const celulas = quadro?.celulas ?? [];
  const celula = celulas.find((c) => c.codigo === abaAtiva) ?? celulas[0] ?? null;

  // D-09: iniciar fora de ordem não pede confirmação; o corpo é sempre vazio.
  async function iniciar(cartao: KanbanCartaoTablet): Promise<void> {
    if (!credencial) return;
    if (!atorRef.current) {
      const acao = {
        tipo: 'iniciar' as const,
        executar: () => iniciar(cartao),
      };
      acaoPendenteRef.current = acao;
      setAcaoPendente(acao);
      setModalOperadorAberto(true);
      return;
    }
    setEmAndamento(cartao.osId);
    setErro(null);
    try {
      const r = await postarTablet<KanbanTabletResultado>(
        credencial,
        `cartoes/${encodeURIComponent(cartao.osId)}/iniciar`,
        {},
      );
      if (await purgarSeRevogado(r.status, r.reason)) return;
      if (r.status === 401) {
        atorRef.current = null;
        setOperadorNome(null);
        setPodeSolicitar(false);
        const acao = {
          tipo: 'iniciar' as const,
          executar: () => iniciar(cartao),
        };
        acaoPendenteRef.current = acao;
        setAcaoPendente(acao);
        setModalOperadorAberto(true);
        return;
      }
      if (r.status === 409) {
        setErro(r.mensagem ?? 'Este cartão está bloqueado e não pode ser iniciado.');
        return;
      }
      if (!r.ok || !r.dados) {
        setErro('Não foi possível iniciar o cartão.');
        return;
      }
      await carregarQuadro(credencial);
    } catch {
      setErro('Sem conexão. Tente iniciar novamente quando a rede voltar.');
    } finally {
      setEmAndamento(null);
    }
  }

  async function pausar(input: { tipoInterrupcaoId?: string; motivoOutro?: string }): Promise<void> {
    if (!credencial || !pausando) return;
    if (!atorRef.current) {
      const acao = {
        tipo: 'pausar' as const,
        executar: () => pausar(input),
      };
      acaoPendenteRef.current = acao;
      setAcaoPendente(acao);
      setModalOperadorAberto(true);
      return;
    }
    setEmAndamento(pausando.osId);
    setErroPausa(null);
    try {
      const r = await postarTablet<KanbanTabletResultado>(
        credencial,
        `cartoes/${encodeURIComponent(pausando.osId)}/pausar`,
        input,
      );
      if (await purgarSeRevogado(r.status, r.reason)) {
        setPausando(null);
        return;
      }
      if (r.status === 401) {
        atorRef.current = null;
        setOperadorNome(null);
        setPodeSolicitar(false);
        const acao = {
          tipo: 'pausar' as const,
          executar: () => pausar(input),
        };
        acaoPendenteRef.current = acao;
        setAcaoPendente(acao);
        setModalOperadorAberto(true);
        return;
      }
      if (!r.ok) {
        setErroPausa(r.mensagem ?? 'Não foi possível pausar o cartão.');
        return;
      }
      setPausando(null);
      await carregarQuadro(credencial);
    } catch {
      setErroPausa('Sem conexão. Tente pausar novamente quando a rede voltar.');
    } finally {
      setEmAndamento(null);
    }
  }

  async function concluir(
    cartao: KanbanCartaoTablet,
    valores: ValoresConclusao,
    identificadorIdempotente: string,
    celulaCodigo?: string,
  ): Promise<void> {
    if (emAndamento !== null) return;
    const cred = credencial;
    if (!cred) return;

    // Falha de rede: o apontamento sempre vale — vai para a fila offline (ADR-006, D-23).
    async function guardarOffline(): Promise<void> {
      if (!atorRef.current) {
        setErroConclusao('Identifique-se com seu PIN antes de apontar offline.');
        const acao = {
          tipo: 'concluir' as const,
          executar: () => concluir(cartao, valores, identificadorIdempotente, celulaCodigo),
        };
        acaoPendenteRef.current = acao;
        setAcaoPendente(acao);
        setModalOperadorAberto(true);
        return;
      }
      await enfileirarEvento(
        montarEventoOffline(
          cartao.osId,
          { ...valores, identificadorIdempotente, actorUserId: atorRef.current, celulaCodigo },
          cred!.deviceId,
        ),
      );
      setConcluindo(null);
      setRegistroCelula(null);
      setAvisoOffline(AVISO_OFFLINE);
      await atualizarPendencias();
      void sincronizarAgora();
    }

    if (!atorRef.current && !semRede()) {
      const acao = {
        tipo: 'concluir' as const,
        executar: () => concluir(cartao, valores, identificadorIdempotente, celulaCodigo),
      };
      acaoPendenteRef.current = acao;
      setAcaoPendente(acao);
      setModalOperadorAberto(true);
      return;
    }

    setEmAndamento(cartao.osId);
    setErroConclusao(null);

    try {
      if (semRede()) {
        await guardarOffline();
        return;
      }
      let r: RespostaTablet<KanbanTabletResultado>;
      try {
        const motivoNc = valores.motivoNc.trim();
        r = await postarTablet<KanbanTabletResultado>(cred, `cartoes/${encodeURIComponent(cartao.osId)}/concluir`, {
          quantidadeBoa: valores.quantidadeBoa,
          quantidadeNaoConforme: valores.quantidadeNaoConforme,
          ...(motivoNc ? { motivoNc } : {}),
          identificadorIdempotente,
          ...(celulaCodigo ? { celulaCodigo } : {}),
        });
      } catch {
        await guardarOffline();
        return;
      }
      if (await purgarSeRevogado(r.status, r.reason)) {
        setConcluindo(null);
        setRegistroCelula(null);
        return;
      }
      if (r.status === 401) {
        atorRef.current = null;
        setOperadorNome(null);
        setPodeSolicitar(false);
        const acao = {
          tipo: 'concluir' as const,
          executar: () => concluir(cartao, valores, identificadorIdempotente, celulaCodigo),
        };
        acaoPendenteRef.current = acao;
        setAcaoPendente(acao);
        setModalOperadorAberto(true);
        return;
      }
      if (respostaEncerraConclusao(r.status)) chavesConclusaoRef.current.delete(cartao.osId);
      if (!r.ok) {
        // D-17: outra cabine registrou antes (403) ou a lista mudou (409): recarrega a lista e explica.
        if (celulaCodigo && (r.status === 403 || r.status === 409)) {
          setConcluindo(null);
          setRegistroCelula(null);
          await carregarQuadro(cred);
          setErro(r.status === 403 ? AVISO_CARTAO_FORA_DA_LISTA : (r.mensagem ?? AVISO_CARTAO_FORA_DA_LISTA));
          return;
        }
        setErroConclusao(r.mensagem ?? 'Não foi possível registrar o apontamento.');
        return;
      }
      setConcluindo(null);
      setRegistroCelula(null);
      await carregarQuadro(cred);
    } finally {
      setEmAndamento(null);
    }
  }

  function renderAcoes(cartao: KanbanCartaoTablet): ReactElement | null {
    const acoes = acoesDoCartao(cartao);
    // Pedido de material do lote/OP do cartão (LOT_OP): independe do estado do cartão, só da permissão.
    if (acoes.length === 0 && !podeSolicitar) return null;
    const ocupado = emAndamento === cartao.osId;
    const aguardandoEnvio = pendenciasOffline.has(cartao.osId);
    return (
      <>
        {acoes.includes('iniciar') && (
          <button
            type="button"
            disabled={ocupado}
            onClick={() => void iniciar(cartao)}
            className={`${CLASSE_BOTAO} bg-emerald-700 hover:bg-emerald-600 active:bg-emerald-800`}
          >
            Iniciar
          </button>
        )}
        {acoes.includes('pausar') && (
          <button
            type="button"
            disabled={ocupado}
            onClick={() => {
              setErroPausa(null);
              setPausando(cartao);
            }}
            className={`${CLASSE_BOTAO} bg-amber-600 hover:bg-amber-500 active:bg-amber-700`}
          >
            Pausar
          </button>
        )}
        {acoes.includes('concluir') && (
          <button
            type="button"
            disabled={ocupado || aguardandoEnvio}
            onClick={() => {
              setErroConclusao(null);
              setRegistroCelula(null);
              setChaveConclusao(
                obterChaveConclusao(chavesConclusaoRef.current, cartao.osId, pendenciasOffline),
              );
              setConcluindo(cartao);
            }}
            className={`${CLASSE_BOTAO} bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700`}
          >
            {aguardandoEnvio ? 'Aguardando envio' : 'Concluir'}
          </button>
        )}
        <BotaoSolicitarMaterialCartao podeSolicitar={podeSolicitar} cartao={cartao} onAbrir={setPedidoMaterial} />
      </>
    );
  }

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8 select-none"
      data-theme="dark-premium"
      data-density="touch"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        <ColetorBarraIdentidade
          operadorNome={operadorNome}
          onSolicitarIdentificacao={() => setModalOperadorAberto(true)}
          onSessaoEncerrada={() => {
            atorRef.current = null;
            setOperadorNome(null);
            setPodeSolicitar(false);
          }}
        />
        <div className="flex justify-end">
          <FilaLocalBadge />
        </div>
        {credencial && (
          <TabletTempoReal credencial={credencial} onAtualizar={recarregar} onNaoAutorizado={recarregar} />
        )}

        {avisoSync && (
          <div className="p-4 bg-red-950/80 border border-red-500/50 text-red-200 rounded-xl text-sm" role="alert">
            {avisoSync}
          </div>
        )}
        {avisoOffline && (
          <div className="p-4 bg-amber-950/60 border border-amber-500/40 text-amber-200 rounded-xl text-sm" role="status">
            {avisoOffline}
          </div>
        )}
        {revogado && (
          <div className="p-4 bg-red-950/80 border border-red-500/50 text-red-200 rounded-xl text-sm" role="alert">
            Este dispositivo foi revogado ou expirou. Peça a um administrador para configurar o tablet novamente.
          </div>
        )}
        {erro && (
          <div className="p-4 bg-red-950/80 border border-red-500/50 text-red-200 rounded-xl text-sm" role="alert">
            {erro}
          </div>
        )}

        {inicializado && !credencial ? (
          <div className="py-12 text-center space-y-4 bg-slate-900/40 rounded-2xl border border-slate-800/80">
            <p className="text-base text-slate-300">Este tablet não está vinculado a nenhuma célula.</p>
            <a
              href="/coletor/kanban/configurar"
              className="inline-flex items-center min-h-[64px] px-6 bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-sm rounded-xl"
            >
              Configurar este tablet
            </a>
          </div>
        ) : carregando ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="ui-skeleton h-32 w-full rounded-2xl" />
            ))}
          </div>
        ) : celula ? (
          <main className="space-y-4">
            {celulas.length > 1 && (
              <div role="tablist" aria-label="Células do tablet" className="flex flex-wrap gap-2">
                {celulas.map((c) => (
                  <button
                    key={c.codigo}
                    type="button"
                    role="tab"
                    aria-selected={c.codigo === celula.codigo}
                    onClick={() => {
                      setAbaAtiva(c.codigo);
                      setVisao('fila');
                    }}
                    className={`min-h-[64px] px-6 rounded-xl font-bold text-base touch-manipulation border ${
                      c.codigo === celula.codigo
                        ? 'bg-emerald-700 border-blue-400 text-white'
                        : 'bg-slate-900 border-slate-700 text-slate-300'
                    }`}
                  >
                    {c.nome}
                  </button>
                ))}
              </div>
            )}

            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-3">
              <h1 className="text-2xl font-bold text-white tracking-tight">{celula.nome}</h1>
              {rotuloAguardandoAlocacao(celula.aguardandoAlocacaoNoProcesso) && (
                <span className="text-sm font-bold text-slate-300 bg-slate-800 border border-slate-700 rounded-full px-3 py-1">
                  {rotuloAguardandoAlocacao(celula.aguardandoAlocacaoNoProcesso)}
                </span>
              )}
            </header>

            <BotaoSolicitarMaterialSetor podeSolicitar={podeSolicitar} celula={celula} onAbrir={setPedidoMaterial} />

            {/* D-14: a fila é a visão padrão; "Etapas anteriores (N)" é um botão separado. */}
            <div className="flex flex-wrap gap-2" role="group" aria-label="Visão da célula">
              <button
                type="button"
                aria-pressed={visao === 'fila'}
                onClick={() => setVisao('fila')}
                className={`min-h-[64px] px-6 rounded-xl font-bold text-base touch-manipulation border ${
                  visao === 'fila'
                    ? 'bg-emerald-700 border-emerald-400 text-white'
                    : 'bg-slate-900 border-slate-700 text-slate-300'
                }`}
              >
                Fila ({celula.cartoes.length})
              </button>
              <button
                type="button"
                aria-pressed={visao === 'etapas-anteriores'}
                onClick={() => setVisao('etapas-anteriores')}
                className={`min-h-[64px] px-6 rounded-xl font-bold text-base touch-manipulation border ${
                  visao === 'etapas-anteriores'
                    ? 'bg-emerald-700 border-emerald-400 text-white'
                    : 'bg-slate-900 border-slate-700 text-slate-300'
                }`}
              >
                {rotuloEtapasAnteriores(celula.etapasAnteriores?.length ?? 0)}
              </button>
            </div>

            {visao === 'etapas-anteriores' ? (
              <TabletEtapasAnteriores
                key={celula.codigo}
                itens={celula.etapasAnteriores ?? []}
                emAndamento={emAndamento}
                aguardandoEnvio={(osId) => pendenciasOffline.has(osId)}
                onRegistrar={(item) => {
                  setErroConclusao(null);
                  setRegistroCelula(celula.codigo);
                  setChaveConclusao(obterChaveConclusao(chavesConclusaoRef.current, item.osId, pendenciasOffline));
                  setConcluindo(item);
                }}
              />
            ) : celula.cartoes.length === 0 ? (
              <div className="py-12 text-center text-sm text-slate-400 bg-slate-900/40 rounded-2xl border border-slate-800/80">
                Nenhum cartão na fila da sua célula.
              </div>
            ) : (
              celula.cartoes.map((cartao) => (
                <TabletCartao key={cartao.osId} cartao={cartao} acoes={renderAcoes(cartao)} />
              ))
            )}
          </main>
        ) : null}
      </div>

      <TabletOperadorModal
        aberto={modalOperadorAberto}
        credencial={credencial}
        acaoPendenteDescricao={
          acaoPendente
            ? acaoPendente.tipo === 'concluir'
              ? 'Concluir apontamento'
              : acaoPendente.tipo === 'iniciar'
                ? 'Iniciar cartão'
                : 'Pausar cartão'
            : null
        }
        onSincronizar={sincronizarParaModal}
        onFechar={() => {
          setModalOperadorAberto(false);
          setAcaoPendente(null);
          acaoPendenteRef.current = null;
        }}
        onAutenticado={(usuario) => {
          setModalOperadorAberto(false);
          atorRef.current = usuario.id;
          setOperadorNome(usuario.displayName);
          void obterSessaoAtual()
            .then((s) => setPodeSolicitar(podeSolicitarMaterial(s)))
            .catch(() => undefined);
          void sincronizarAgora();
          if (acaoPendenteRef.current) {
            const acao = acaoPendenteRef.current;
            acaoPendenteRef.current = null;
            setAcaoPendente(null);
            void acao.executar();
          }
        }}
      />

      {concluindo && chaveConclusao && (
        <TabletConcluirSheet
          key={concluindo.osId}
          cartao={concluindo}
          modo={registroCelula ? 'registrar' : 'concluir'}
          identificadorIdempotente={chaveConclusao}
          enviando={emAndamento !== null}
          erro={erroConclusao}
          onFechar={() => {
            setConcluindo(null);
            setRegistroCelula(null);
          }}
          onConfirmar={(valores, idem) => void concluir(concluindo, valores, idem, registroCelula ?? undefined)}
        />
      )}

      {pedidoMaterial && (
        <SolicitarMaterialDialog
          aberto
          onAbertoChange={(aberto) => {
            if (!aberto) setPedidoMaterial(null);
          }}
          {...pedidoMaterial}
          onSolicitado={recarregar}
        />
      )}

      {pausando && (
        <TabletPausarSheet
          key={pausando.osId}
          aberto
          motivos={motivos}
          enviando={emAndamento !== null}
          erro={erroPausa}
          onFechar={() => setPausando(null)}
          onConfirmar={(input) => void pausar(input)}
        />
      )}
    </div>
  );
}
